/** 行動パネル：状態、予約のタイムライン、操作ボタン（docs/06_ui-ux.md 2・4 章） */
import { formatDuration, formatKm } from '../../core/units';
import { el } from '../layout';

export type RouteLegView =
  | {
      kind: 'hike';
      toLabel: string;
      toColor: string;
      durationS: number;
      lengthM: number;
      ascentM: number;
      descentM: number;
      arriveClock: string;
    }
  | {
      kind: 'flight';
      toLabel: string;
      toColor: string;
      durationS: number;
      flightS: number;
      lengthM: number;
      reachable: boolean;
      /** 届かない理由：far＝高さが足りない、ridge＝途中の尾根を越えられない */
      blocked: 'far' | 'ridge' | null;
      /** 予報の風から見た離陸の見通し */
      outlook: 'good' | 'tailwind' | 'strong';
      arriveClock: string;
    };

export interface RoutePanelModel {
  phase: 'planning' | 'running' | 'other';
  editing: boolean;
  awaitingFlightTarget: boolean;
  canFly: boolean;
  statusChip: string;
  statusText: string;
  legs: RouteLegView[];
  error: string | null;
  autoStyle: 'attack' | 'defend';
}

export interface RoutePanelActions {
  edit(): void;
  confirm(): void;
  cancel(): void;
  undo(): void;
  clear(): void;
  fly(): void;
  start(): void;
  toggleStyle(): void;
}

export function createRoutePanel(
  host: HTMLElement,
  actions: RoutePanelActions,
): { render(m: RoutePanelModel): void } {
  const status = el('div', 'panel__status');
  const timeline = el('ol', 'timeline');
  const buttons = el('div', 'panel__buttons');
  const button = (label: string, key: string, onClick: () => void, cls = ''): HTMLButtonElement => {
    const b = el('button', `btn ${cls}`);
    b.append(el('span', 'btn__label', label), el('kbd', 'btn__key', key));
    b.addEventListener('click', onClick);
    buttons.appendChild(b);
    return b;
  };
  const styleBtn = button('', 'Q', actions.toggleStyle);
  const flyBtn = button('ここから飛ぶ', 'F', actions.fly);
  const editBtn = button('ルート編集', 'R', actions.edit);
  const undoBtn = button('1つ戻す', '右クリック', actions.undo);
  const clearBtn = button('クリア', 'Del', actions.clear);
  const cancelBtn = button('取消', 'Esc', actions.cancel);
  const confirmBtn = button('確定', 'Enter', actions.confirm, 'btn--primary');
  const startBtn = button('スタート', 'Enter', actions.start, 'btn--primary');
  const styleLabel = styleBtn.querySelector('.btn__label') as HTMLElement;
  styleBtn.title = 'オートパイロットの飛び方（攻め＝雲底まで上がらず先へ、守り＝しっかり上がってから進む）';
  host.append(status, timeline, buttons);
  let lastLegCount = 0;

  return {
    render(m) {
      status.replaceChildren(
        el(
          'span',
          `chip ${m.editing ? 'chip--edit' : m.phase === 'planning' ? 'chip--plan' : ''}`,
          m.statusChip,
        ),
        el('span', 'panel__status-text', m.statusText),
      );
      if (m.error) status.appendChild(el('span', 'panel__status-error', m.error));

      const scrollLeft = timeline.scrollLeft;
      timeline.replaceChildren();
      if (m.awaitingFlightTarget) {
        timeline.appendChild(
          el(
            'li',
            'timeline__empty timeline__empty--flight',
            'フライトの目標をクリック（点線の円は滑空だけで届く範囲）。右クリックで取り消し',
          ),
        );
      } else if (m.legs.length === 0) {
        timeline.appendChild(
          el(
            'li',
            'timeline__empty',
            m.editing || m.phase === 'planning'
              ? 'ノードをクリックしてルートを延ばす。離陸地（▲）で F を押すとフライトを予約'
              : '予約なし（R でルート編集）',
          ),
        );
      }
      for (const leg of m.legs) {
        const li = el('li', `timeline__leg timeline__leg--${leg.kind}`);
        li.style.setProperty('--leg-color', leg.toColor);
        if (leg.kind === 'hike') {
          li.append(
            el('span', 'timeline__kind', 'ハイク'),
            el('span', 'timeline__to', `→ ${leg.toLabel}`),
            el('span', 'timeline__time', formatDuration(leg.durationS)),
            el(
              'span',
              'timeline__meta',
              `${formatKm(leg.lengthM)}  ↑${Math.round(leg.ascentM)}  ↓${Math.round(leg.descentM)}`,
            ),
            el('span', 'timeline__eta', `着 ${leg.arriveClock}`),
          );
        } else {
          li.append(
            el('span', 'timeline__kind', 'フライト'),
            el('span', 'timeline__to', `→ ${leg.toLabel}`),
            el('span', 'timeline__time', formatDuration(leg.durationS)),
            el(
              'span',
              `timeline__meta${leg.reachable ? '' : ' timeline__meta--warn'}`,
              `${formatKm(leg.lengthM)}  準備+飛行${Math.round(leg.flightS / 60)}分+格納${
                leg.blocked === 'ridge'
                  ? '  尾根を越えられない見込み（サーマル頼み）'
                  : leg.blocked === 'far'
                    ? '  届かない見込み（サーマル頼み）'
                    : ''
              }`,
            ),
            ...(leg.outlook === 'good'
              ? []
              : [
                  el(
                    'span',
                    'timeline__warn',
                    leg.outlook === 'tailwind'
                      ? '追い風予報：離陸に失敗しやすい'
                      : '強風予報：飛べなければ歩いて下る',
                  ),
                ]),
            el('span', 'timeline__eta', `着 ${leg.arriveClock}`),
          );
        }
        timeline.appendChild(li);
      }
      timeline.scrollLeft = m.legs.length > lastLegCount ? timeline.scrollWidth : scrollLeft;
      lastLegCount = m.legs.length;

      const planning = m.phase === 'planning';
      const editingLike = m.editing || planning;
      styleLabel.textContent = m.autoStyle === 'attack' ? '攻め' : '守り';
      flyBtn.hidden = !m.canFly;
      editBtn.hidden = editingLike || m.phase === 'other';
      undoBtn.hidden = !editingLike;
      clearBtn.hidden = !editingLike;
      cancelBtn.hidden = !m.editing || planning;
      confirmBtn.hidden = !m.editing || planning;
      startBtn.hidden = !planning;
      confirmBtn.disabled = m.error !== null;
      startBtn.disabled = m.error !== null;
    },
  };
}
